import { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField, Checkbox, Paper } from "@mui/material";
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  PieSeries,
  AccumulationTooltip,
  AccumulationLegend,
  Inject,
} from "@syncfusion/ej2-react-charts";
import dayjs from "dayjs";
import api from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";
import ChartControls from "../../components/Channel/ChartControls";
import MultiLineAvgChart from "../../components/charts3/MultiLineAvgChart";
import { formatPercent } from "../../utils/formatNumber";
import "./ChannelShare.css";

const PIE_COLORS = [
  "#3366CC", "#DC3912", "#FF9900", "#109618",
  "#990099", "#0099C6", "#DD4477", "#66AA00",
  "#B82E2E", "#316395", "#994499", "#22AA99",
  "#AAAA11", "#6633CC", "#E67300",
];

// The "Channel Share" report — ported from the old Analytics.Extended admin
// tool (admnewsmcr.cshtml). Same idea (compare several channels' Average
// Active TV side by side) and the same three granularities, but no fixed
// channel list any more: whoever opens this picks whichever channels they
// want to compare, the same way UserManagement.jsx already lets a superadmin
// pick from every channel and everyone else pick from their own — and the
// same searchable-multi-select pattern YoutubeTrackerGrid.jsx already uses
// for the same job.
//
// A sidebar item like any other: it only shows up, and the data only loads,
// for a channel+user pair that both carry "channelshare" in their sidebar
// items (see ChannelSidebar.jsx and Channel.jsx). channelId rides along on
// every request as that license check; it has nothing to do with which
// channels end up being compared.

const MODE_TO_RTYPE = { minute: "m", hour: "h", day: "d" };

// A, B, C, … Z, AA, AB, … — spreadsheet-column style, so it never runs out
// regardless of how many channels are in the picker.
function indexToLetters(i) {
  let n = i + 1;
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function readDarkMode() {
  try {
    return !!JSON.parse(localStorage.getItem("adm_panel_user") || "{}").darkmode;
  } catch {
    return false;
  }
}

// The channel selection is remembered in the browser, per channel, for a day
// — not written anywhere on the server. Long enough that coming back to this
// tab later today (or tomorrow morning) still has your comparison set up;
// short enough that a selection from weeks ago doesn't quietly resurface.
const SELECTION_TTL_MS = 24 * 60 * 60 * 1000;
const selectionKey = (channelId) => `adm_channelshare_selection_${channelId}`;

function readSavedSelection(channelId) {
  try {
    const raw = localStorage.getItem(selectionKey(channelId));
    if (!raw) return [];
    const { ids, savedAt } = JSON.parse(raw);
    if (!Array.isArray(ids) || typeof savedAt !== "number") return [];
    if (Date.now() - savedAt > SELECTION_TTL_MS) return [];
    return ids;
  } catch {
    return [];
  }
}

function writeSavedSelection(channelId, ids) {
  try {
    if (ids.length === 0) {
      localStorage.removeItem(selectionKey(channelId));
      return;
    }
    localStorage.setItem(
      selectionKey(channelId),
      JSON.stringify({ ids, savedAt: Date.now() })
    );
  } catch {
    /* private browsing or a full store — the selection just won't be remembered */
  }
}

export default function ChannelShare({ channelId }) {
  const { user } = useAuth();
  const isDark = readDarkMode();
  const pieRef = useRef(null);

  const [mode, setMode] = useState("minute");

  const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(yesterday);
  const [endDate, setEndDate] = useState(yesterday);
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");

  // Who's pickable, and what's picked. Superadmins get every channel in the
  // system (/allci); everyone else only their own (/ci) — the same split
  // UserManagement.jsx already uses for its channel picker.
  const [pickableChannels, setPickableChannels] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => readSavedSelection(channelId));

  // A different channel context (a navigation, not just a re-render) gets its
  // own remembered selection, not whatever the previous one left behind.
  useEffect(() => {
    setSelectedIds(readSavedSelection(channelId));
  }, [channelId]);

  useEffect(() => {
    if (channelId) writeSavedSelection(channelId, selectedIds);
  }, [channelId, selectedIds]);

  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Screen-share mode: swaps every real channel name shown on this page for
  // "Channel A/B/C…". Component state only — never written anywhere, gone the
  // moment you navigate away or reload. Searching and picking still work on
  // real names; only what's displayed changes.
  const [masked, setMasked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadPickable() {
      try {
        if (user?.role === "SUPERADMIN") {
          const res = await api.get("/allci");
          if (cancelled) return;
          setPickableChannels(
            (res.data || []).map((c) => ({
              id: String(c?.id || c?._id || c?.i || ""),
              name: c?.channelName || c?.n || "",
            }))
          );
          return;
        }

        const ids = user?.channels || [];
        if (!ids.length) {
          setPickableChannels([]);
          return;
        }
        const res = await api.get(`/ci?i=${ids.join("-")}`);
        if (cancelled) return;
        setPickableChannels(
          (res.data || []).map((c) => ({
            id: String(c?.i || ""),
            name: c?.n || "",
          }))
        );
      } catch {
        if (!cancelled) setPickableChannels([]);
      }
    }

    loadPickable();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const pickableById = useMemo(() => {
    const map = {};
    pickableChannels.forEach((c) => {
      map[c.id] = c.name || c.id;
    });
    return map;
  }, [pickableChannels]);

  // Stable per-channel letter, independent of selection order or fetch order
  // — sorted by id, so "Channel A" doesn't jump to a different channel just
  // because the selection changed.
  const maskLabels = useMemo(() => {
    const map = {};
    [...pickableChannels]
      .sort((a, b) => a.id.localeCompare(b.id))
      .forEach((c, i) => {
        map[c.id] = `Channel ${indexToLetters(i)}`;
      });
    return map;
  }, [pickableChannels]);

  const displayName = (id) =>
    id === "TOTAL" ? "TOTAL" : masked ? maskLabels[id] || id : pickableById[id] || id;

  useEffect(() => {
    if (!channelId || selectedIds.length === 0) {
      setPoints([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    const rtype = MODE_TO_RTYPE[mode] || "m";
    const params = { rtype, channelId, channels: selectedIds.join(",") };
    if (rtype === "m") {
      params.date = startDate;
      params.startTime = startTime;
      params.endTime = endTime;
    } else {
      params.startDate = startDate;
      params.endDate = endDate;
    }

    api
      .get("/admin/news-share", { params })
      .then((res) => {
        if (cancelled) return;
        setPoints(Array.isArray(res.data?.points) ? res.data.points : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.status === 403
            ? "You don't have access to one or more of the selected channels."
            : "Could not load the report. Please try again."
        );
        setPoints([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, mode, startDate, endDate, startTime, endTime, selectedIds]);

  const series = useMemo(() => {
    const withTotal = [...selectedIds, "TOTAL"];
    return withTotal.map((id) => ({
      name: displayName(id),
      data: points.map((p) => ({ x: p.time, active: p.values?.[id] ?? 0 })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, selectedIds, pickableById, masked, maskLabels]);

  // Each channel's share of the whole window — the sum of its own points
  // over the sum of every selected channel's points, as a percentage. TOTAL
  // itself isn't a slice; it's what the shares are shares of.
  const pieData = useMemo(() => {
    const sums = selectedIds.map((id) => ({
      id,
      name: displayName(id),
      sum: points.reduce((acc, p) => acc + (p.values?.[id] ?? 0), 0),
    }));
    const grandTotal = sums.reduce((acc, s) => acc + s.sum, 0);
    if (grandTotal <= 0) return [];

    return sums
      .filter((s) => s.sum > 0)
      .map((s) => ({ key: s.name, value: (s.sum / grandTotal) * 100 }))
      .sort((a, b) => b.value - a.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, selectedIds, pickableById, masked, maskLabels]);

  // Syncfusion doesn't always pick up a new dataSource array on its own.
  useEffect(() => {
    pieRef.current?.refresh();
  }, [pieData]);

  const selectedOptions = pickableChannels.filter((c) => selectedIds.includes(c.id));

  // Some channels share a display name (genuinely different tracked
  // channels — different accounts, same public name, e.g. "Al Jazeera
  // English" onboarded for more than one customer). Their id is shown
  // alongside the name wherever that happens, so two rows that look
  // identical are still tell-apart-able.
  const nameCounts = useMemo(() => {
    const counts = {};
    pickableChannels.forEach((c) => {
      const key = (c.name || c.id).trim().toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [pickableChannels]);
  const hasNameCollision = (c) => nameCounts[(c.name || c.id).trim().toLowerCase()] > 1;

  // MUI's own default filter matches on getOptionLabel(option) already, but
  // doing it explicitly here means exactly one thing decides what "now"
  // matches: the channel's own name (falling back to its id only when it has
  // no name), nothing else.
  const filterChannelOptions = (options, { inputValue }) => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.name || o.id || "").toLowerCase().includes(q));
  };

  const removeChannel = (id) =>
    setSelectedIds((prev) => prev.filter((x) => x !== id));

  // Matches YoutubeTrackerGrid.jsx's own Autocomplete popup styling, so a
  // picker that does the same job looks the same wherever it shows up.
  const PickerPaper = (props) => (
    <Paper
      {...props}
      sx={{
        bgcolor: isDark ? "#1a1f2e" : "#ffffff",
        border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
        backgroundImage: "none",
        "& .MuiAutocomplete-listbox": {
          bgcolor: isDark ? "#1a1f2e" : "#ffffff",
          "& .MuiAutocomplete-option": {
            color: isDark ? "#e0e0e0" : "#111827",
            '&[aria-selected="true"]': {
              bgcolor: isDark ? "rgba(144,202,249,0.15)" : "rgba(25,118,210,0.08)",
            },
          },
        },
      }}
    />
  );

  return (
    <div className="channel-share-page">
      <ChartControls
        channelId={channelId}
        mode={mode}
        onModeChange={setMode}
        allowedModes={["minute", "hour", "day"]}
        startDateInput={startDate}
        endDateInput={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
      />

      <div className="channel-share-toolbar">
        <span className="toolbar-label">Channels</span>
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          className="channel-share-autocomplete"
          PaperComponent={PickerPaper}
          options={pickableChannels}
          value={selectedOptions}
          getOptionLabel={(o) => o.name || o.id}
          // Without this, MUI keys each option row by getOptionLabel(option)
          // — the channel's NAME. With ~400 channels, several genuinely share
          // a name (the same public channel tracked separately for more than
          // one account), so React saw duplicate keys and, per its own
          // warning, started showing the wrong row for some of them: that was
          // the real cause of "now" and "tv8" turning up unrelated channels —
          // not the search logic, which was correct all along.
          getOptionKey={(o) => o.id}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={filterChannelOptions}
          onChange={(_, v) => setSelectedIds(v.map((x) => x.id))}
          renderTags={() => null}
          slotProps={{ listbox: { sx: { maxHeight: 320 } } }}
          renderOption={(props, option, { selected }) => {
            const { key, ...liProps } = props;
            return (
              <li key={key} {...liProps}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0 }} />
                <span className="channel-share-option-text">
                  {displayName(option.id)}
                  {!masked && hasNameCollision(option) && (
                    <span className="channel-share-option-id">{option.id}</span>
                  )}
                </span>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search channels…" />
          )}
        />

        <button
          type="button"
          className={`channel-share-mask ${masked ? "is-on" : ""}`}
          onClick={() => setMasked((m) => !m)}
          title={
            masked
              ? "Showing generic names — click to show real names"
              : "Replace channel names with generic labels (Channel A, B, …) for screen sharing. Not saved anywhere."
          }
        >
          <i className={`fa-solid ${masked ? "fa-eye-slash" : "fa-eye"}`}></i>
          {masked ? "Names hidden" : "Mask names"}
        </button>

        {selectedIds.length > 0 && (
          <button
            type="button"
            className="channel-share-clear"
            onClick={() => setSelectedIds([])}
          >
            Clear
          </button>
        )}
      </div>

      {selectedOptions.length > 0 && (
        <div className="channel-share-chips">
          {selectedOptions.map((c) => (
            <span key={c.id} className="channel-share-chip">
              {displayName(c.id)}
              <button
                type="button"
                aria-label={`Remove ${displayName(c.id)}`}
                onClick={() => removeChannel(c.id)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {error && <div className="channel-share-error">{error}</div>}

      <div className="chart-box">
        {selectedIds.length === 0 ? (
          <div className="chart-empty">Pick one or more channels to compare.</div>
        ) : loading ? (
          <div className="chart-empty">Loading…</div>
        ) : points.length > 0 ? (
          <MultiLineAvgChart title="Average Active TV" series={series} />
        ) : (
          <div className="chart-empty">No data available</div>
        )}
      </div>

      {/* Share of the window shown above, as a percentage — the pie answers
          "who had how much of it", the line above answers "when". */}
      {selectedIds.length > 0 && !loading && pieData.length > 0 && (
        <div className="chart-box channel-share-pie-box">
          <h3 className="chart-title">Share of total</h3>
          <AccumulationChartComponent
            ref={pieRef}
            width="100%"
            height="380px"
            background="transparent"
            legendSettings={{
              visible: true,
              position: "Right",
              alignment: "Center",
              textStyle: {
                size: "12px",
                color: isDark ? "#e5e7eb" : "#0f172a",
                fontWeight: "500",
              },
            }}
            tooltip={{ enable: true }}
            tooltipRender={(args) => {
              args.text = `${args.point.x} : ${formatPercent(args.point.y, 1)}`;
            }}
          >
            <Inject services={[PieSeries, AccumulationTooltip, AccumulationLegend]} />
            <AccumulationSeriesCollectionDirective>
              <AccumulationSeriesDirective
                dataSource={pieData}
                xName="key"
                yName="value"
                type="Pie"
                radius="78%"
                palettes={PIE_COLORS}
                dataLabel={{
                  visible: true,
                  position: "Outside",
                  name: "value",
                  font: {
                    fontWeight: "600",
                    size: "12px",
                    color: isDark ? "#e5e7eb" : "#0f172a",
                  },
                  connectorStyle: {
                    length: "14px",
                    width: 1,
                    color: isDark ? "#475569" : "#cbd5e1",
                  },
                  enableSmartLabels: true,
                }}
                dataLabelRender={(args) => {
                  args.text = formatPercent(args.point.y, 1);
                }}
              />
            </AccumulationSeriesCollectionDirective>
          </AccumulationChartComponent>
        </div>
      )}
    </div>
  );
}
